import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { useEffect, useState, useCallback, memo, useRef } from "react";
import { OrderDetail, OrderDetailWithBatches, ProductIds } from "../../models/orders/order-list-model";
import NewItemDeleteDialog from "./new-item-delete-dialog";
import { calculateItemSum, calculateItemTotal, calculateTotalTax } from "../products/product-calculation";
import NewCreateInvoiceTotal from "./new-create-invoice-total";
import { useTranslation } from "react-i18next";
import posService from "../../services/pos/pos.service";
import { Message } from "primereact/message";
import { Dropdown } from "primereact/dropdown";
import TestReportService from "../../services/TestReport/TestReport.service";
import { Dialog } from "primereact/dialog";
import InlineQrScanner from "../qr/InlineQrScanner";
import { Toast } from "primereact/toast";
import orderServices from "../../services/orders/orderservice";

const NewCreateInvoiceItems = memo((props: any) => {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState<OrderDetailWithBatches[]>([]);
  const [checkoutItem, setCheckoutItem] = useState<OrderDetail | null>(null);
  const [totalTaxable, setTotalTaxable] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);
  const [disAmt, setDisAmt] = useState<number>(0);
  const [stockMap, setStockMap] = useState<Record<number, number | null>>({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [scanOpen, setScanOpen] = useState(false);
  const toast = useRef<Toast>(null);
  const [scanTargetProductId, setScanTargetProductId] = useState<number | null>(null);
  const [batchOptionsMap, setBatchOptionsMap] = useState<Record<number, any[]>>({});
  const [quantityChanges, setQuantityChanges] = useState<
    Record<
      number,
      {
        removedQty: number;
        reason: string | undefined;
        originalQty: number;
        type: "delete" | "reduce";
      }
    >
  >({});

  const reasonOptions = [
    { label: "Item Shortage", value: "SHORTAGE" },
    { label: "Not Required Anymore", value: "NOT_REQUIRED" }
  ];
  const [removedProducts, setRemovedProducts] = useState<Record<number, OrderDetailWithBatches>>({});
  const [originalQtyMap, setOriginalQtyMap] = useState<Record<number, number>>({});
  const prevProductIds = useRef<Set<number>>(new Set());
  const [interactedProducts, setInteractedProducts] = useState<Set<number>>(new Set());
  const [rewardData, setRewardData] = useState({ redeemedPoints: 0, finalPayable: 0, redeemMorePoints: false });
  const [rewardSwitchEnabled, setRewardSwitchEnabled] = useState(true);

  // Derive redeemMorePoints: true if any product was added/increased, false if any was deleted/reduced
  useEffect(() => {
    // No original data yet — skip
    if (Object.keys(originalQtyMap).length === 0) return;

    let hasIncrease = false;
    let hasDecrease = false;

    for (const item of orderData) {
      if (item.orderDetailID === 0) {
        // Brand-new product added during invoice creation
        hasIncrease = true;
        break;
      }
      const original = originalQtyMap[item.productID];
      if (original === undefined) continue;
      if (item.pendingQuantity > original) {
        hasIncrease = true;
        break;
      }
      if (item.pendingQuantity < original) {
        hasDecrease = true;
      }
    }

    // Check for fully deleted products (they are removed from orderData)
    const currentProductIds = new Set(orderData.map(i => i.productID));
    for (const pid of Object.keys(originalQtyMap)) {
      if (!currentProductIds.has(Number(pid))) {
        hasDecrease = true;
        break;
      }
    }

    const originalPointsUsed = props.orderedItems?.rewardPointsUsed ?? props.orderedItems?.rewardPoints ?? 0;
    const invoicedPointsUsed = props.orderedItems?.invoicedRewardPoint ?? 0;
    const newlyTurnedOn = rewardSwitchEnabled && originalPointsUsed === 0;
    const hasPriorInvoicedPoints = invoicedPointsUsed > 0;
    const unfulfilledOrderPoints = Math.max(0, originalPointsUsed - invoicedPointsUsed);
    const usingMorePoints = rewardData.redeemedPoints > unfulfilledOrderPoints;

    // true for ANY modification (added, increased, decreased, deleted) OR newly turned on OR past invoice points OR using more points; false when nothing changed OR switch is OFF
    const redeemMorePoints = rewardSwitchEnabled && (hasIncrease || hasDecrease || newlyTurnedOn || hasPriorInvoicedPoints || usingMorePoints);

    setRewardData(prev => {
      if (prev.redeemMorePoints === redeemMorePoints) return prev;
      return { ...prev, redeemMorePoints };
    });
  }, [orderData, originalQtyMap, rewardSwitchEnabled, props.orderedItems, rewardData.redeemedPoints]);

  useEffect(() => {
    props.onRewardChange?.(rewardData);
  }, [rewardData]);

  const syncBatchesWithQuantity = useCallback((batches: any[], productID: number, newQty: number) => {
    if (!batches.length) return batches;

    const nextBatches = batches.map(batch => ({ ...batch }));
    let remaining = newQty;

    // First pass: keep existing allocations up to each batch's stock limit.
    for (let i = 0; i < nextBatches.length; i++) {
      const batch = nextBatches[i];
      const availableStock =
        (batchOptionsMap[productID] || []).find(opt => opt.value === batch.batchId)
          ?.availableStock ?? Infinity;

      const allowedQty = Math.min(Number(batch.quantity) || 0, availableStock, remaining);
      nextBatches[i] = { ...batch, quantity: allowedQty };
      remaining -= allowedQty;
    }

    // Second pass: when product quantity increases, absorb the remainder into
    // already selected batches that still have spare available stock.
    if (remaining > 0) {
      for (let i = 0; i < nextBatches.length && remaining > 0; i++) {
        const batch = nextBatches[i];
        if (!batch.batchId) continue;

        const availableStock =
          (batchOptionsMap[productID] || []).find(opt => opt.value === batch.batchId)
            ?.availableStock ?? Infinity;

        const currentQty = Number(batch.quantity) || 0;
        const extraCapacity = Math.max(0, availableStock - currentQty);
        const addQty = Math.min(extraCapacity, remaining);

        if (addQty > 0) {
          nextBatches[i] = { ...batch, quantity: currentQty + addQty };
          remaining -= addQty;
        }
      }
    }

    return nextBatches;
  }, [batchOptionsMap]);

  const fetchAndPopulateBatches = useCallback(async (productID: number) => {
    if (!props.orderedItems?.posID || productID === ProductIds.EMPTY_BOTTLE) return;

    try {
      const batches = await TestReportService.getBatchesByPosAndProduct(
        props.orderedItems.posID,
        productID
      );

      const options = batches.map(b => ({
        label: b.batchCode,
        value: b.batchId,
        availableStock: b.availableStock
      }));

      setBatchOptionsMap(prev => ({ ...prev, [productID]: options }));
      return options;
    } catch (error) {
      console.error("Failed to load batches:", error);
      setBatchOptionsMap(prev => ({ ...prev, [productID]: [] }));
      return [];
    }
  }, [props.orderedItems?.posID]);

  const calculateItemsTotal = useCallback((items = orderData) => {
    return items.reduce(
      (total, item) => total + item.pendingQuantity * (item.unitPrice || 0),
      0
    );
  }, [orderData]);

  const calculateTotalTaxItems = useCallback((items = orderData) => {
    return items.reduce(
      (total, item) => total + item.pendingQuantity * (item.unitPrice || 0) * (item.gst / 100),
      0
    );
  }, [orderData]);

  useEffect(() => {
    if (!props.orderedItems?.orderDetails) return;

    setOrderData(prev => {
      const incoming: OrderDetail[] = props.orderedItems.orderDetails;

      // ── Initial load: prev is empty, take everything from incoming ──
      if (prev.length === 0) {
        setOriginalQtyMap(existing => {
          const updated = { ...existing };
          incoming.forEach(i => {
            if (updated[i.productID] === undefined) {
              updated[i.productID] = i.pendingQuantity;
            }
          });
          return updated;
        });
        return incoming.map((item: OrderDetail) => ({
          ...item,
          batches: (item as OrderDetailWithBatches).batches ?? []
        }));
      }

      const prevIds = new Set(prev.map(p => p.productID));
      const incoming_ids = new Set(incoming.map((i: OrderDetail) => i.productID));

      const hasNewProducts = incoming.some((i: OrderDetail) => !prevIds.has(i.productID));
      const hasRemovedProducts = prev.some(p => !incoming_ids.has(p.productID));

      if (!hasNewProducts && !hasRemovedProducts) {
        return prev;
      }
      setOriginalQtyMap(existing => {
        const updated = { ...existing };
        incoming.forEach((i: OrderDetail) => {
          if (updated[i.productID] === undefined) {
            updated[i.productID] = i.pendingQuantity;
          }
        });
        return updated;
      });

      return incoming.map((item: OrderDetail) => {
        const existing = prev.find(p => p.productID === item.productID);
        return {
          ...item,
          // ✔ Always keep our locally-modified quantity for existing items
          pendingQuantity: existing !== undefined ? existing.pendingQuantity : item.pendingQuantity,
          batches: (item as OrderDetailWithBatches).batches ?? existing?.batches ?? []
        };
      });
    });
  }, [props.orderedItems?.orderDetails]);

  useEffect(() => {
    const fetchStockData = async () => {
      if (!props.orderedItems?.posID || !props.orderedItems?.orderDetails?.length)
        return;

      const map: Record<number, number | null> = {};

      await Promise.all(
        props.orderedItems.orderDetails.map(async (item: OrderDetail) => {
          try {
            const stockResponse = await posService.GetnewPOSProductdetails(
              props.orderedItems.posID,
              item.productID
            );
            map[item.productID] = stockResponse?.[0]?.availableStock ?? null;
          } catch {
            map[item.productID] = null;
          }
        })
      );
      setStockMap(map);
    };
    fetchStockData();
  }, [props.orderedItems?.posID, props.orderedItems?.orderDetails?.length]);

  useEffect(() => {
    const newExpandedRows: Record<number, boolean> = {};
    let hasNewItems = false;

    orderData.forEach((item) => {
      if (!prevProductIds.current.has(item.productID)) {
        if (item.productID !== ProductIds.EMPTY_BOTTLE) {
          newExpandedRows[item.productID] = true;
          hasNewItems = true;
        }
      }
    });

    if (hasNewItems) {
      setExpandedRows((prev) => ({ ...prev, ...newExpandedRows }));
    }

    prevProductIds.current = new Set(orderData.map((item) => item.productID));
  }, [orderData]);

  const EMPTY_BOTTLE_PRODUCT_ID = ProductIds.EMPTY_BOTTLE;

  const productNameBodyTemplate = (product: OrderDetail) => {
    const availableStock = stockMap[product.productID];
    const isInsufficient = availableStock !== undefined && availableStock !== null && product.pendingQuantity > availableStock;

    return (
      <div>
        <div className="font-semibold">{product.productName}</div>
        {product.productID !== EMPTY_BOTTLE_PRODUCT_ID && isInsufficient && (
          <Message
            severity="warn"
            text={`Insufficient stock, current stock is ${availableStock}`}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    const taxable = calculateItemsTotal();
    const tax = calculateTotalTaxItems();

    setTotalTaxable(taxable);
    setTotalTax(tax);

    props.updateItems(orderData, taxable, tax, disAmt);

    const isEmpty =
      orderData.length === 0 ||
      orderData.every(item => item.pendingQuantity === 0);

    props.onOrderDataEmptyChange?.(isEmpty);

    const hasInsufficientStock = orderData.some(item => {
      if (item.productID === EMPTY_BOTTLE_PRODUCT_ID) return false;
      const stock = stockMap[item.productID];
      return stock != null && item.pendingQuantity > stock;
    });

    props.onInsufficientStockChange?.(hasInsufficientStock);
  }, [orderData, disAmt, stockMap]);

  // 🔥 LOGIC: Validate batches on POS change
  useEffect(() => {
    const refreshAllExpandedBatches = async () => {
      const posID = props.orderedItems?.posID;
      if (!posID) return;

      const expandedIds = Object.keys(expandedRows || {}).map(Number);
      if (!expandedIds.length) return;

      const results = await Promise.all(
        expandedIds.map(async (pid) => {
          const options = await fetchAndPopulateBatches(pid);
          return { pid, options };
        })
      );

      setOrderData(curr => curr.map(item => {
        const res = results.find(r => r.pid === item.productID);
        if (!res || !res.options) return item;

        const defaultQty = item.minOrderQty || 1;
        return {
          ...item,
          batches: item.batches.map(b => {
            const match = res.options!.find((opt: any) => opt.value === b.batchId);
            if (match) {
              const clampedQty = Math.min(b.quantity, match.availableStock ?? Infinity);
              return { ...b, quantity: clampedQty || Math.min(item.pendingQuantity, defaultQty) };
            }
            return { ...b, batchId: null, quantity: Math.min(item.pendingQuantity, b.quantity || defaultQty) };
          })
        };
      }));
    };

    refreshAllExpandedBatches();
  }, [props.orderedItems?.posID, fetchAndPopulateBatches]);

  const updateDiscount = useCallback((value: number) => {
    setDisAmt(value);
    const taxable = calculateItemsTotal();
    const tax = calculateTotalTaxItems();
    props.updateItems(orderData, taxable, tax, value);
  }, [orderData, calculateItemsTotal, calculateTotalTaxItems, props.updateItems]);

  const imageBodyTemplate = (item: any) => {
    return (
      <img
        src={
          item.url === undefined || item.url === null
            ? process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png"
            : item.url
        }
        alt=""
        style={{ objectFit: 'contain', borderRadius: '4px', width: '50px', height: '50px', display: 'block' }}
      />
    );
  };

  const unitPriceBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(product.unitPrice);
  };

  const updateItemsQuantityValue = (
    productID: number,
    value: number | null
  ) => {
    setOrderData(prev =>
      prev.map(item => {
        if (item.productID !== productID) return item;

        const minQty = item.minOrderQty || 1;
        const step = item.orderMultiples || 1;

        let qty = value ?? minQty;
        qty = Math.max(minQty, qty);
        qty = Math.round(qty / step) * step;

        // 🔗 Replicate to batch allocation if there is exactly one batch
        const batches = syncBatchesWithQuantity([...item.batches], item.productID, qty);

        return { ...item, pendingQuantity: qty, batches };
      })
    );
  };

  const quantityTemplate = (product: OrderDetail) => {
    const isPaymentCharged = props.paymentAlreadyCharged === true;

    const minQty = product.minOrderQty || 1;
    const step = product.orderMultiples || 1;
    const originalQty = Number(
      originalQtyMap[product.productID] ?? product.quantity
    );

    const fpMod = (a: number, b: number): number => {
      if (!b) return 0;
      return Math.abs(a - Math.round(a / b) * b);
    };

    const isMisalignedItem =
      product.orderDetailID > 0 &&
      (
        originalQty < minQty ||                              // below new minimum
        fpMod(originalQty - minQty, step) > 1e-9             // doesn't fit the minQty + N*step grid
      );

    const needsStaticDisplay =
      isMisalignedItem &&
      originalQty < minQty &&
      !interactedProducts.has(product.productID);

    const handleFirstSnap = () => {
      setInteractedProducts(prev => { const s = new Set(prev); s.add(product.productID); return s; });
      setOrderData(prev => prev.map(item => {
        if (item.productID !== product.productID) return item;
        const batches = syncBatchesWithQuantity([...item.batches], item.productID, minQty);
        return { ...item, pendingQuantity: minQty, batches };
      }));
      // If snapping DECREASES from original (rare edge-case): track the reduction
      const original = originalQtyMap[product.productID];
      if (product.orderDetailID > 0 && minQty < original) {
        setQuantityChanges(prev => ({
          ...prev,
          [product.productID]: { removedQty: original - minQty, reason: undefined, originalQty: original, type: "reduce" }
        }));
        props.onQuantityChangePending?.(true);
      }
      // Always show toast on first interaction
      toast.current?.show({
        severity: "warn",
        summary: "Product Details Changed",
        detail: `Product settings (Min Order Qty / Order Multiples) for [${product.productName}] were updated after this order was placed. The original order quantities are retained as-is — please review before generating the invoice.`,
        life: 6000
      });
    };

    return (
      <div className="flex product-item product-wrapper">
        <div className="product-list-quantity">
          {needsStaticDisplay ? (
            // ── Static display: shows original ordered qty, no InputNumber, no auto-snap ──
            // On +/- click → handleFirstSnap snaps to minQty and switches to InputNumber.
            <div style={{ display: 'flex', alignItems: 'stretch', width: 120 }}>
              <Button
                icon="pi pi-minus"
                severity="danger"
                onClick={handleFirstSnap}
                disabled={isPaymentCharged}
                style={{ width: '2.357rem', height: '2.357rem', borderRadius: '3px 0 0 3px', padding: 0, flexShrink: 0 }}
              />
              <span style={{
                flex: 1,
                textAlign: 'center',
                border: '1px solid #ced4da',
                borderLeft: 'none',
                borderRight: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                color: '#495057',
                background: '#fff',
                minWidth: 0
              }}>
                {product.pendingQuantity}
              </span>
              <Button
                icon="pi pi-plus"
                severity="success"
                onClick={handleFirstSnap}
                disabled={isPaymentCharged}
                style={{ width: '2.357rem', height: '2.357rem', borderRadius: '0 3px 3px 0', padding: 0, flexShrink: 0 }}
              />
            </div>
          ) : (
            // ── Interactive InputNumber: min enforced natively, no negative display ──
            <InputNumber
              inputId={String(product.productID)}
              style={{ width: 120 }}
              value={product.pendingQuantity}
              showButtons={!isPaymentCharged}
              buttonLayout="horizontal"
              step={step}
              decrementButtonClassName="p-button-danger"
              className="quantity-border"
              incrementButtonClassName="p-button-success"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
              min={minQty}
              disabled={isPaymentCharged}
              onInput={(e: any) =>
                updateItemsQuantityValue(
                  product.productID,
                  e.target.value ? Number(e.target.value) : null
                )
              }
              onValueChange={(e) => {
                const newQty = e.value ?? null;
                const original = originalQtyMap[product.productID];

                updateItemsQuantityValue(product.productID, newQty);

                // Track quantity reductions below the originally-ordered quantity
                // ❗ ONLY for original order items (orderDetailID > 0)
                if (product.orderDetailID > 0 && newQty !== null && newQty < original) {
                  setQuantityChanges(prev => ({
                    ...prev,
                    [product.productID]: {
                      removedQty: original - newQty,
                      reason: undefined,
                      originalQty: original,
                      type: "reduce"
                    }
                  }));
                  props.onQuantityChangePending?.(true);
                } else {
                  setQuantityChanges(prev => {
                    const updated = { ...prev };
                    delete updated[product.productID];
                    props.onQuantityChangePending?.(Object.keys(updated).length > 0);
                    return updated;
                  });
                }

                if (isMisalignedItem) {
                  toast.current?.show({
                    severity: "warn",
                    summary: "Product Details Changed",
                    detail: `Product settings (Min Order Qty / Order Multiples) for [${product.productName}] were updated after this order was placed. The original order quantities are retained as-is — please review before generating the invoice.`,
                    life: 6000
                  });
                }
              }}
            />
          )}
        </div>

        <div className="product-list-button ml-2">
          {!isPaymentCharged && (
            <Button
              icon="pi pi-trash"
              rounded
              severity="danger"
              onClick={() => deleteItem(product)}
              raised
            />
          )}
        </div>
      </div>
    );
  };

  const deleteItem = (item: OrderDetail) => {
    const removedQty = item.pendingQuantity;

    if (item.orderDetailID === 0) {
      setOrderData(prev =>
        prev.filter(p => p.productID !== item.productID)
      );
      return;
    }

    const itemWithBatches: OrderDetailWithBatches = {
      ...item,
      batches: (item as OrderDetailWithBatches).batches ?? []
    };

    setRemovedProducts(prev => ({
      ...prev,
      [item.productID]: itemWithBatches
    }));

    // ❗ Only track delete reason for original items
    if (item.orderDetailID > 0) {
      setQuantityChanges(prev => ({
        ...prev,
        [item.productID]: {
          removedQty,
          reason: undefined,
          originalQty: item.pendingQuantity,
          type: "delete"
        }
      }));
      props.onQuantityChangePending?.(true);
    }
    setOrderData(prev => [...prev]);
  };

  const hideNotifyDialog = (value: boolean) => {
    if (value && checkoutItem) {
      setOrderData(prev =>
        prev.filter(product => product.productID !== checkoutItem.productID)
      );
    }
    setIsNotifyVisible(false);
  };

  const handleQuantityChangeSubmit = async () => {
    const allSelected = Object.values(quantityChanges).every(c => c.reason);

    if (!allSelected) {
      toast.current?.show({
        severity: "warn",
        summary: "Selection Required",
        detail: "Please select a reason for all changes",
        life: 2000
      });
      return;
    }

    let deleteCount = 0;
    let reduceCount = 0;

    for (const [productId, change] of Object.entries(quantityChanges)) {
      const product =
        orderData.find(p => p.productID === Number(productId)) ||
        removedProducts[Number(productId)];

      if (!product) continue;

      const newQty = change.originalQty - change.removedQty;

      // 🔹 1️⃣ SHORTAGE → NO API
      if (change.reason === "SHORTAGE") {
        if (change.type === "delete" || newQty <= 0) {
          deleteCount++;
          setOrderData(prev => prev.filter(p => p.productID !== Number(productId)));
        } else {
          reduceCount++;
          setOrderData(prev =>
            prev.map(p =>
              p.productID === Number(productId)
                ? {
                  ...p,
                  pendingQuantity: newQty,
                  batches: syncBatchesWithQuantity([...p.batches], p.productID, newQty)
                }
                : p
            )
          );
        }
        continue;
      }

      // 🔹 2️⃣ NOT_REQUIRED → CALL API
      // 🔹 2️⃣ NOT_REQUIRED → CALL CancelOrderDetail API
      if (change.reason === "NOT_REQUIRED") {
        const orderDetailId = product.orderDetailID;
        const removedQty = change.removedQty;

        const taxable = product.unitPrice * removedQty;
        const tax = (product.unitPrice * removedQty * product.gst) / 100;
        const gross = taxable + tax - (product.discountAmount || 0);

        await orderServices.cancelOrderDetail({
          orderDetailID: orderDetailId,
          cancelledQty: removedQty,
          unitPrice: product.unitPrice,
          taxableAmount: taxable,
          taxAmount: tax,
          discountAmount: product.discountAmount || 0,
          grossAmount: gross,
          reason: "NOT_REQUIRED",
        });

        if (change.type === "delete" || newQty <= 0) {
          deleteCount++;
          setOrderData(prev => prev.filter(p => p.productID !== Number(productId)));
        } else {
          reduceCount++;
          setOrderData(prev =>
            prev.map(p =>
              p.productID === Number(productId)
                ? {
                  ...p,
                  pendingQuantity: newQty,
                  batches: syncBatchesWithQuantity([...p.batches], p.productID, newQty)
                }
                : p
            )
          );
        }
        continue;
      }

      if (change.type === "delete" || newQty <= 0) {
        deleteCount++;
        setOrderData(prev => prev.filter(p => p.productID !== Number(productId)));
      } else {
        reduceCount++;
        setOrderData(prev =>
          prev.map(p =>
            p.productID === Number(productId)
              ? {
                ...p,
                pendingQuantity: newQty,
                batches: syncBatchesWithQuantity([...p.batches], p.productID, newQty)
              }
              : p
          )
        );
      }
    }

    setQuantityChanges({});
    props.onQuantityChangePending?.(false);

    // 🔥 Smart Success Message
    let message = "";
    if (deleteCount > 0) message += `${deleteCount} item(s) deleted successfully. `;
    if (reduceCount > 0) message += `${reduceCount} item(s) quantity decreased successfully.`;

    toast.current?.show({
      severity: "success",
      summary: "Changes Applied",
      detail: message.trim(),
      life: 2500
    });
  };

  const taxableAmountBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(
      calculateItemSum(product.unitPrice, product.pendingQuantity)
    );
  };

  const taxAmountBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(
      calculateTotalTax(product.unitPrice, product.pendingQuantity, product.gst)
    );
  };

  const grossWithScanTemplate = (product: OrderDetail) => {
    return (
      <div className="flex align-items-center justify-content-end gap-2">
        <span>
          {Helpers.formatAmountInINR(
            Math.round(
              calculateItemTotal(
                product.unitPrice,
                product.pendingQuantity,
                calculateTotalTax(
                  product.unitPrice,
                  product.pendingQuantity,
                  product.gst
                ),
                0
              )
            )
          )}
        </span>

        {product.productID !== EMPTY_BOTTLE_PRODUCT_ID && (
          <Button
            icon="pi pi-camera"
            className={`p-button-text ${btnBorder}`}
            tooltip="Scan QR"
            onClick={() => {
              setScanTargetProductId(product.productID);
              setExpandedRows(prev => ({
                ...prev,
                [product.productID]: true
              }));
              setScanOpen(true);
            }}
          />
        )}
      </div>
    );
  };

  const getSelectedBatchIds = (
    product: OrderDetailWithBatches,
    currentIndex: number
  ) => {
    return product.batches
      .filter((_, i) => i !== currentIndex)
      .map(b => b.batchId)
      .filter(Boolean);
  };

  const addBatch = (productID: number) => {
    setOrderData(prev =>
      prev.map(item => {
        if (item.productID !== productID) return item;

        const currentAllocated = item.batches.reduce(
          (sum, b) => sum + (b.quantity || 0),
          0
        );
        const remaining = Math.max(0, item.pendingQuantity - currentAllocated);

        return {
          ...item,
          batches: [...item.batches, { batchId: null, quantity: remaining }]
        };
      })
    );
  };

  const updateBatch = (
    productID: number,
    index: number,
    field: "batchId" | "quantity",
    value: any
  ) => {
    setOrderData(prev =>
      prev.map(item => {
        if (item.productID !== productID) return item;

        const batches = [...item.batches];

        // 🟢 If quantity is being updated
        if (field === "quantity") {
          const batchId = batches[index].batchId;

          const availableStock =
            batchOptionsMap[productID]?.find(b => b.value === batchId)
              ?.availableStock ?? Infinity;

          if (value > availableStock) {
            // ❗ SHOW TOAST LIMIT MESSAGE
            toast.current?.show({
              severity: "warn",
              summary: "Stock Limit Reached",
              detail: `Maximum available quantity for this batch is ${availableStock}`,
              life: 2500
            });
          }

          const otherQty = batches.reduce(
            (sum, b, i) => (i === index ? sum : sum + (b.quantity || 0)),
            0
          );

          const maxAllowedOverall = Math.max(
            0,
            item.pendingQuantity - otherQty
          );

          const finalQty = Math.min(
            Number(value) || 0,
            availableStock,
            maxAllowedOverall
          );

          batches[index] = {
            ...batches[index],
            quantity: finalQty
          };

          return { ...item, batches };
        }

        // 🟢 If batchId changed
        if (field === "batchId") {
          if (item.batches.some((b, i) => i !== index && b.batchId === value)) {
            toast.current?.show({
              severity: "warn",
              summary: "Duplicate Batch",
              detail: "This batch is already selected for this product",
              life: 2000
            });
            return item;
          }

          const nextItem = { ...item, batches };
          const { effectiveMax } = getBatchEffectiveMax(nextItem, index, value);
          const currentQty = Number(batches[index].quantity) || 0;

          batches[index] = {
            ...batches[index],
            batchId: value,
            quantity: value ? Math.min(currentQty, effectiveMax) || effectiveMax : currentQty
          };

          return { ...item, batches };
        }

        return item;
      })
    );
  };

  const removeBatch = (productID: number, index: number) => {
    setOrderData(prev =>
      prev.map(item =>
        item.productID === productID
          ? { ...item, batches: item.batches.filter((_, i) => i !== index) }
          : item
      )
    );
  };

  const getBatchEffectiveMax = (
    product: OrderDetailWithBatches,
    index: number,
    batchId: number | null
  ) => {
    const otherQty = product.batches.reduce(
      (sum, b, i) => (i === index ? sum : sum + (b.quantity || 0)),
      0
    );

    const maxAllowed = Math.max(0, product.pendingQuantity - otherQty);
    const availableStock =
      (batchOptionsMap[product.productID] || []).find(opt => opt.value === batchId)
        ?.availableStock ?? Infinity;

    return {
      maxAllowed,
      availableStock,
      effectiveMax: Math.min(maxAllowed, availableStock)
    };
  };

  const stepBatchQuantity = (
    product: OrderDetailWithBatches,
    index: number,
    direction: "increment" | "decrement"
  ) => {
    const batch = product.batches[index];
    if (!batch) return;

    const step = product.orderMultiples || 1;
    const { availableStock, effectiveMax } = getBatchEffectiveMax(
      product,
      index,
      batch.batchId
    );
    const currentQty = Number(batch.quantity) || 0;

    if (direction === "increment") {
      if (currentQty >= effectiveMax || effectiveMax <= 0) {
        if (availableStock !== Infinity && availableStock < product.pendingQuantity) {
          toast.current?.show({
            severity: "warn",
            summary: "Stock Limit Reached",
            detail: `Maximum available quantity for this batch is ${availableStock}`,
            life: 2500
          });
        }
        return;
      }

      const nextQty = Math.min(currentQty + step, effectiveMax);
      updateBatch(product.productID, index, "quantity", nextQty);
      return;
    }

    const isStockConstrained =
      availableStock !== Infinity && availableStock < product.pendingQuantity;

    const nextQty =
      currentQty > effectiveMax
        ? effectiveMax
        : isStockConstrained
          ? effectiveMax
          : Math.max(0, currentQty - step);

    updateBatch(product.productID, index, "quantity", nextQty);
  };

  const extractBatchId = (rawText: string): string | null => {
    if (!rawText) return null;

    const text = rawText.trim();
    let extracted: string | null = null;

    // 1️⃣ URL → take last path segment
    try {
      if (/^https?:\/\//i.test(text)) {
        const url = new URL(text);
        const parts = url.pathname.split("/").filter(Boolean);
        extracted = parts.length ? parts[parts.length - 1] : null;
      }
    } catch {
      // ignore
    }

    // 2️⃣ Legacy QR: QR|TR|<batch>
    if (!extracted && text.includes("|")) {
      const parts = text.split("|");
      extracted = parts[parts.length - 1] || null;
    }

    if (!extracted) {
      extracted = text;
    }

    const match = extracted.match(/^B0*(\d+)$/i);
    if (match) {
      return match[1];
    }

    if (/^\d+$/.test(extracted)) {
      return extracted;
    }

    return null;
  };

  const handleQrScan = (rawText: string) => {
    const batchNo = extractBatchId(rawText);

    if (!batchNo || !scanTargetProductId) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid QR",
        detail: "Unable to extract batch information",
      });
      return;
    }

    const productBatches = batchOptionsMap[scanTargetProductId] || [];

    const matchedBatch = productBatches.find(
      b => String(b.value) === String(batchNo)
    );

    if (!matchedBatch) {
      toast.current?.show({
        severity: "warn",
        summary: "Batch not found",
        detail: `Batch ${batchNo} not available for this product`,
      });
      return;
    }

    const batchId = matchedBatch.value;
    const availableStock = matchedBatch.availableStock ?? Infinity;

    const product = orderData.find(p => p.productID === scanTargetProductId);
    if (!product) return;

    const totalPendingQty = product.pendingQuantity;

    // 1️⃣ CHECK: Total allocated qty
    const currentAllocated = product.batches.reduce(
      (sum, b) => sum + (b.quantity || 0),
      0
    );

    if (currentAllocated >= totalPendingQty) {
      toast.current?.show({
        severity: "warn",
        summary: "Quantity Limit Exceeded",
        detail: `Item requires only ${totalPendingQty} units. All quantities are already allocated.`,
        life: 2500
      });
      setScanOpen(false);
      return;
    }

    // 2️⃣ CHECK: Batch-level available stock limit
    const existingBatch = product.batches.find(b => b.batchId === batchId);
    const existingBatchQty = existingBatch?.quantity || 0;

    if (existingBatchQty + 1 > availableStock) {
      toast.current?.show({
        severity: "warn",
        summary: "Batch Stock Limit",
        detail: `Maximum available quantity for this batch is ${availableStock}.`,
        life: 2500
      });
      setScanOpen(false);
      return;
    }

    // 3️⃣ EVERYTHING VALID → ADD / INCREMENT BATCH
    setOrderData(prev =>
      prev.map(item => {
        if (item.productID !== scanTargetProductId) return item;

        const batchIndex = item.batches.findIndex(b => b.batchId === batchId);

        const step = item.orderMultiples || 1;
        if (batchIndex >= 0) {
          return {
            ...item,
            batches: item.batches.map((b, i) =>
              i === batchIndex
                ? { ...b, quantity: b.quantity + step }
                : b
            )
          };
        }

        return {
          ...item,
          batches: [...item.batches, { batchId, quantity: step }]
        };
      })
    );

    // 🎉 Success toast only when added successfully
    toast.current?.show({
      severity: "success",
      summary: "Batch Assigned",
      detail: `Batch ${batchNo} added`,
      life: 2000,
    });

    setScanOpen(false);
    setScanTargetProductId(null);
  };

  const getBatchError = (item: OrderDetailWithBatches) => {
    if (item.productID === EMPTY_BOTTLE_PRODUCT_ID) return null;
    if (!item.batches.length) return "No batches selected";

    const totalBatchQty = item.batches.reduce(
      (sum, b) => sum + (b.quantity || 0),
      0
    );

    if (totalBatchQty > item.pendingQuantity) {
      return "Batch quantity exceeds item quantity";
    }

    if (totalBatchQty < item.pendingQuantity) {
      return `Batch quantity mismatch (Required: ${item.pendingQuantity})`;
    }

    return null;
  };

  const btnBorder = "border border-gray-300 rounded-md";

  const batchExpansionTemplate = (product: OrderDetailWithBatches) => {
    const batchError = getBatchError(product);
    const totalAllocated = product.batches.reduce(
      (sum, b) => sum + (b.quantity || 0),
      0
    );

    if (product.productID === EMPTY_BOTTLE_PRODUCT_ID) {
      return (
        <div className="ml-4 p-3 surface-50 border-round text-green-700">
          Batch allocation is handled automatically for empty bottles.
        </div>
      );
    }

    const isFullyAllocated = totalAllocated >= product.pendingQuantity;

    return (
      <div className="ml-4 p-3 surface-50 border-round">
        <div className="font-semibold mb-2 text-sm">Batch Allocation</div>

        {product.batches.map((batch, index) => {
          const otherQty = product.batches.reduce(
            (sum, b, i) => (i === index ? sum : sum + (b.quantity || 0)),
            0
          );

          const maxAllowed = Math.max(0, product.pendingQuantity - otherQty);

          const availableStock =
            (batchOptionsMap[product.productID] || []).find(opt => opt.value === batch.batchId)
              ?.availableStock ?? Infinity;

          const isStockExceeded = batch.quantity > availableStock;
          const hasBatchStockConstraint =
            availableStock !== Infinity && availableStock < product.pendingQuantity;
          const showBatchStockWarning =
            hasBatchStockConstraint && totalAllocated < product.pendingQuantity;

          const selectedBatchIds = getSelectedBatchIds(product, index);

          return (
            <div key={index} className="mb-2">
              <div
                className="flex align-items-center flex-wrap"
                style={{ gap: "0.875rem" }}
              >
                <Dropdown
                  value={batch.batchId}
                  options={(batchOptionsMap[product.productID] || []).filter(
                    opt => !selectedBatchIds.includes(opt.value)
                  )}
                  placeholder="Select Batch"
                  filter
                  showClear
                  filterPlaceholder="Search batch"
                  disabled={!!batch.batchId}
                  onChange={(e) =>
                    updateBatch(product.productID, index, "batchId", e.value)
                  }
                  style={{
                    border: "2px solid #71d896ff",   // ✅ always highlighted
                    borderRadius: 6,
                    height: 36,
                    minWidth: 150,
                    background: "#f0fdf4",
                    fontWeight: 500
                  }}
                />
                <div
                  className={isStockExceeded ? "p-invalid" : ""}
                  style={{
                    display: "flex",
                    alignItems: "stretch",
                    height: 36,
                    marginRight: 4,
                    borderRadius: 6,
                    overflow: "hidden"
                  }}
                >
                  <Button
                    icon="pi pi-minus"
                    type="button"
                    onClick={() => stepBatchQuantity(product, index, "decrement")}
                    style={{
                      borderRadius: "6px 0 0 6px",
                      height: 36,
                      width: "2.6rem",
                      margin: 0
                    }}
                  />
                  <InputNumber
                    value={batch.quantity}
                    min={0}
                    max={Math.min(maxAllowed, availableStock)}
                    minFractionDigits={0}
                    maxFractionDigits={3}
                    step={product.orderMultiples || 1}
                    showButtons={false}
                    onInput={(e: any) =>
                      updateBatch(
                        product.productID,
                        index,
                        "quantity",
                        e.target.value ? Number(e.target.value) : 0
                      )
                    }
                    onValueChange={(e) => {
                      const attempted = e.value ?? 0;
                      if (attempted >= availableStock && attempted > (batch.quantity || 0)) {
                        toast.current?.show({
                          severity: "warn",
                          summary: "Stock Limit Reached",
                          detail: `Maximum available quantity for this batch is ${availableStock}`,
                          life: 2500
                        });
                      }
                      updateBatch(
                        product.productID,
                        index,
                        "quantity",
                        Math.min(attempted, availableStock)
                      );
                    }}
                    style={{
                      borderRadius: 0,
                      height: 36,
                      background: "#f0fdf4",
                      fontWeight: 600,
                      width: 140,
                      margin: 0
                    }}
                    inputStyle={{
                      width: "100%",
                      height: 36,
                      lineHeight: "36px",
                      textAlign: "center",
                      fontWeight: 600,
                      borderRadius: 0,
                      padding: 0,
                      textIndent: 0
                    }}
                  />
                  <Button
                    icon="pi pi-plus"
                    type="button"
                    onClick={() => stepBatchQuantity(product, index, "increment")}
                    disabled={batch.quantity >= Math.min(maxAllowed, availableStock)}
                    style={{
                      borderRadius: "0 6px 6px 0",
                      height: 36,
                      width: "2.6rem",
                      margin: 0
                    }}
                  />
                </div>

                <Button
                  icon="pi pi-times"
                  severity="danger"
                  outlined
                  onClick={() => removeBatch(product.productID, index)}
                  style={{
                    marginLeft: 6,
                    width: 36,
                    height: 36,
                    padding: 0,
                    flexShrink: 0
                  }}
                />
              </div>

              {showBatchStockWarning && (
                <small className="p-error ml-1">
                  Maximum available stock is {availableStock}
                </small>
              )}
            </div>
          );
        })}

        {product.productID !== EMPTY_BOTTLE_PRODUCT_ID && (
          <Button
            label="Add Batch"
            icon="pi pi-plus"
            text
            className="mt-2"
            disabled={isFullyAllocated}   // ✅ disable when full
            tooltip={
              isFullyAllocated
                ? "All quantities are already allocated to batches"
                : "Add another batch"
            }
            tooltipOptions={{ position: "top" }}
            onClick={() => addBatch(product.productID)}
          />
        )}
        {/* ⚠️ OVERALL BATCH VALIDATION MESSAGE */}
        {batchError && (
          <Message
            severity="warn"
            text={batchError}
            className="mt-3"
          />
        )}
      </div>
    );
  };

  const onRowToggle = (e: any) => {
    setExpandedRows(e.data);
    const expandedIds = Object.keys(e.data || {}).map(Number);
    if (expandedIds.length > 0) {
      const lastExpandedId = expandedIds[expandedIds.length - 1];
      if (!batchOptionsMap[lastExpandedId]) {
        fetchAndPopulateBatches(lastExpandedId);
      }
    }
  };

  useEffect(() => {
    const syncExpandedRows = async () => {
      const expandedIds = Object.keys(expandedRows || {}).map(Number);
      if (!expandedIds.length) return;

      for (const pid of expandedIds) {
        if (!batchOptionsMap[pid]) {
          fetchAndPopulateBatches(pid);
        }
      }
    };
    syncExpandedRows();
  }, [expandedRows, fetchAndPopulateBatches, batchOptionsMap]);

  return (
    <>
      <Toast ref={toast} />
      <DataTable
        value={orderData.filter(x => x.pendingQuantity > 0)}
        expandedRows={expandedRows}
        onRowToggle={onRowToggle}
        rowExpansionTemplate={batchExpansionTemplate}
        dataKey="productID"
        size="small"
      >
        <Column
          expander={(rowData) => rowData.productID !== EMPTY_BOTTLE_PRODUCT_ID}
          style={{ width: "3rem" }}
        />
        <Column header="Image" style={{ width: "5%", marginLeft: "8rem" }} body={imageBodyTemplate}></Column>
        <Column
          field="productName"
          style={{ width: "20%" }}
          header={t("invoices_product_name")}
          body={productNameBodyTemplate}
        ></Column>
        <Column
          field="unitPrice"
          header={t("invoices_product_price")}
          body={unitPriceBodyTemplate}
          bodyStyle={{ textAlign: "center" }}
        ></Column>
        <Column
          field="quantity"
          style={{ width: "5%" }}
          header={t("invoices_product_quantity")}
          body={quantityTemplate}
        ></Column>
        <Column
          field="taxableAmount"
          header={t("invoices_product_amount")}
          body={taxableAmountBodyTemplate}
          bodyStyle={{ textAlign: "right" }}
        ></Column>
        <Column
          field="taxAmount"
          header={t("invoices_product_tax")}
          body={taxAmountBodyTemplate}
          bodyStyle={{ textAlign: "right" }}
        ></Column>
        <Column
          header={t("invoices_product_tot_amount")}
          body={grossWithScanTemplate}
          bodyStyle={{ textAlign: "right" }}
        />
      </DataTable>

      <Dialog
        header="Scan Batch QR"
        visible={scanOpen}
        onHide={() => setScanOpen(false)}
        style={{ width: "350px" }}
        contentStyle={{ padding: "1rem" }}
        onShow={() => { }}
      >
        {scanOpen && (
          <InlineQrScanner
            onScan={handleQrScan}
            onClose={() => setScanOpen(false)}
          />
        )}
      </Dialog>

      <NewItemDeleteDialog
        messageHeader={notifyHeader}
        notifyMessage={notifyMessage}
        isVisible={isNotifyVisible}
        isHide={hideNotifyDialog}
      />

      {Object.keys(quantityChanges).length > 0 && (
        <div
          className="mt-4 p-4 surface-50 border-round"
          style={{ backgroundColor: "#fef3c7", border: "1px solid #fcd34d" }}
        >
          <div className="font-semibold mb-3 flex align-items-center gap-2">
            <i className="pi pi-info-circle" style={{ color: "#d97706" }}></i>
            {Object.values(quantityChanges).some(c => c.type === "delete")
              ? "Deleted Items"
              : "Pending Quantity Changes"
            } ({Object.keys(quantityChanges).length})
          </div>

          <div className="grid" style={{ gap: "1rem" }}>
            {Object.entries(quantityChanges).map(([productId, changeData]) => {
              const change = changeData as {
                removedQty: number;
                reason: string | undefined;
                originalQty: number;
                type: "delete" | "reduce";
              };

              let product = orderData.find(p => p.productID === Number(productId));
              if (!product) product = removedProducts[Number(productId)];
              if (!product) return null;
              if (product.orderDetailID === 0) return null;

              return (
                <div
                  key={productId}
                  className="flex align-items-center gap-3 p-3 surface-ground border-round"
                  style={{ border: "1px solid #ddd" }}
                >
                  <div style={{ flex: 1 }}>
                    <p className="m-0 font-semibold text-sm">{product.productName}</p>
                    {change.type === "reduce" ? (
                      <p className="m-0 text-sm" style={{ color: "#666" }}>
                        Quantity reduced from {change.originalQty} to {change.originalQty - change.removedQty}
                        <span style={{ color: "#dc2626", fontWeight: 500 }}> (-{change.removedQty})</span>
                      </p>
                    ) : (
                      <p className="m-0 text-sm" style={{ color: "#666" }}>
                        Item removed
                        <span style={{ color: "#dc2626", fontWeight: 500 }}>
                          &nbsp;(Qty: {change.originalQty})
                        </span>
                      </p>
                    )}
                  </div>
                  <div style={{ minWidth: "200px" }}>
                    <small style={{ display: "block", color: "#666", marginBottom: "4px" }}>
                      Reason
                    </small>
                    <Dropdown
                      value={change.reason}
                      options={reasonOptions}
                      onChange={(e) => {
                        const reason = e.value;
                        setQuantityChanges(prev => ({
                          ...prev,
                          [Number(productId)]: {
                            ...prev[Number(productId)],
                            reason
                          }
                        }));
                        props.onQuantityChangePending?.(true);
                      }}
                      placeholder="Select reason"
                      className="w-full"
                      style={{ minWidth: "200px" }}
                      showClear={false}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex gap-2 justify-content-end">
            <Button
              label="Discard All Changes"
              severity="secondary"
              onClick={() => {
                setOrderData((prev) =>
                  prev.map((item) => {
                    const change = quantityChanges[item.productID];
                    if (change) {
                      return { ...item, pendingQuantity: change.originalQty };
                    }
                    return item;
                  })
                );
                setQuantityChanges({});
                props.onQuantityChangePending?.(false);
              }}
              outlined
            />
            <Button
              label="Submit Changes"
              icon="pi pi-check"
              onClick={handleQuantityChangeSubmit}
              disabled={Object.values(quantityChanges).some((change: any) => !change.reason)}
            />
          </div>
        </div>
      )}

      {totalTaxable > 0 && (
        <NewCreateInvoiceTotal
          taxableAmount={totalTaxable}
          taxAmount={totalTax}
          orderedItems={props.orderedItems}
          updateDiscountAmount={updateDiscount}
          onRewardChange={(data: { redeemedPoints: number; finalPayable: number; switchEnabled?: boolean }) => {
            setRewardData(prev => ({ ...prev, ...data }));
            if (data.switchEnabled !== undefined) setRewardSwitchEnabled(data.switchEnabled);
          }}
          paymentAlreadyCharged={props.paymentAlreadyCharged}
        />
      )}
    </>
  );
});

export default NewCreateInvoiceItems;
